import { EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { SearchQuery } from '../models/search-query.model';
import { RentalSalesFinderFilter, RENTAL_SALES_AIRCRAFT_MANUFACTURER_OPTIONS, RENTAL_SALES_AIRCRAFT_RENTAL_OPTIONS, RENTAL_SALES_AVIONICS_MANUFACTURER_OPTIONS, RENTAL_SALES_SALES_TYPE_OPTIONS } from '../../shared/models/rental-sales-finder-filter.model';
import { RentalSalesFinderFiltersService } from '../../shared/services/rental-sales-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
/**
 * Filter UI for the Rentals/Sales Finder, mirroring {@link FboFinderFilterListComponent}: collapsible
 * accordion sections with a selected-count badge and a selected-filter chip row; the Sales section nests its
 * manufacturer lists as collapsible subsections. Reads and writes the shared
 * {@link RentalSalesFinderFiltersService} state and emits `filtersChange`.
 */
var RentalSalesFinderFilterListComponent = /** @class */ (function () {
    function RentalSalesFinderFilterListComponent(_rentalSalesFinderFiltersService, _analyticsService) {
        this._rentalSalesFinderFiltersService = _rentalSalesFinderFiltersService;
        this._analyticsService = _analyticsService;
        this.filtersChange = new EventEmitter();
        /**
         * The collapsible facet sections, in display order: the group key on RentalSalesFinderFilter, the header
         * label, and the checkbox options. All groups use OR logic (see filterRentalSales). The Sales section owns
         * no checkboxes directly — Aircraft Sale Type, Aircraft Manufacturers and Avionics Manufacturers all nest under
         * it as equal-level collapsible subsections.
         */
        this.sections = [
            { key: 'aircraftRental', title: 'Aircraft Rental', options: RENTAL_SALES_AIRCRAFT_RENTAL_OPTIONS },
            { key: 'salesSection', title: 'Sales', options: [], subsections: [
                    { key: 'salesTypes', title: 'Aircraft Sale Type', options: RENTAL_SALES_SALES_TYPE_OPTIONS },
                    { key: 'aircraftSalesManufacturers', title: 'Aircraft Manufacturers', options: RENTAL_SALES_AIRCRAFT_MANUFACTURER_OPTIONS },
                    { key: 'avionicsSalesManufacturers', title: 'Avionics Manufacturers', options: RENTAL_SALES_AVIONICS_MANUFACTURER_OPTIONS }
                ] }
        ];
        /**
         * Per-section expand/collapse state. Sections start collapsed; any section that already has selected
         * filters is auto-expanded on first load so restored/active filters are visible. Each header shows a
         * count badge.
         */
        this.expanded = {
            aircraftRental: false,
            salesSection: false,
            salesTypes: false,
            aircraftSalesManufacturers: false,
            avionicsSalesManufacturers: false
        };
        this._expansionInitialized = false;
        /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
        this.maxChips = 5;
        /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
        this.visibleChips = [];
        /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
        this.overflowCount = 0;
        this.rentalSalesFinderFilter = new RentalSalesFinderFilter();
    }
    Object.defineProperty(RentalSalesFinderFilterListComponent.prototype, "nameTooShort", {
        /**
         * True when a business name has been entered but is too short to count toward a search. Mirrors the
         * `rentalSalesKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while
         * the entered name would be ignored.
         */
        get: function () {
            var trimmed = this.rentalSalesFinderFilter.name ? this.rentalSalesFinderFilter.name.trim() : '';
            return trimmed.length > 0 && trimmed.length < 3;
        },
        enumerable: true,
        configurable: true
    });
    RentalSalesFinderFilterListComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._subscription = this._rentalSalesFinderFiltersService.rentalSalesFinderFilter.subscribe(function (f) {
            _this.rentalSalesFinderFilter = f;
            _this._recomputeChips();
            // Auto-expand sections with active filters once (on first load); don't fight the user's manual toggles
            // on subsequent emissions (each checkbox edit re-emits the same filter).
            if (!_this._expansionInitialized) {
                _this._expansionInitialized = true;
                _this.sections.forEach(function (s) {
                    var subsections = s.subsections || [];
                    var subHas = subsections.map(function (sub) { return !!(f && f[sub.key] && f[sub.key].length); });
                    // Sections with no options of their own (e.g. the 'salesSection' wrapper) don't map to a real
                    // FacetGroup, so only look at f[s.key] when the section actually owns checkboxes.
                    var ownHas = s.options.length > 0 && !!(f && f[s.key] && f[s.key].length);
                    _this.expanded[s.key] = ownHas || subHas.some(function (h) { return h; }); // parent opens if anything inside is selected
                    subsections.forEach(function (sub, i) { return _this.expanded[sub.key] = subHas[i]; }); // a subsection opens only if it has selections
                });
            }
        });
        this._analyticsService.pageView('airport_landingpage_rentalsales', 'Rentals/Sales Finder Landing Page filters');
    };
    RentalSalesFinderFilterListComponent.prototype.ngOnDestroy = function () {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    };
    RentalSalesFinderFilterListComponent.prototype.isFacetSelected = function (group, value) {
        return this.rentalSalesFinderFilter[group].indexOf(value) !== -1;
    };
    RentalSalesFinderFilterListComponent.prototype.onToggleFacet = function (group, value, checked) {
        var selected = this.rentalSalesFinderFilter[group];
        var i = selected.indexOf(value);
        if (checked && i === -1) {
            selected.push(value);
        }
        else if (!checked && i !== -1) {
            selected.splice(i, 1);
        }
        this._emit();
    };
    RentalSalesFinderFilterListComponent.prototype.onSelectAll = function (group, options) {
        this.rentalSalesFinderFilter[group] = options.map(function (o) { return o.value; });
        this._emit();
    };
    RentalSalesFinderFilterListComponent.prototype.onClearAll = function (group) {
        this.rentalSalesFinderFilter[group] = [];
        this._emit();
    };
    RentalSalesFinderFilterListComponent.prototype.onNameChange = function () {
        this._emit();
    };
    RentalSalesFinderFilterListComponent.prototype.toggleSection = function (group) {
        this.expanded[group] = !this.expanded[group];
    };
    /** Number of selected filters in a single group (shown as the count badge on that group's header). */
    RentalSalesFinderFilterListComponent.prototype.selectedCount = function (group) {
        var selected = this.rentalSalesFinderFilter[group];
        return selected ? selected.length : 0;
    };
    /**
     * Total selected filters for a parent section — its own options (if any) plus any nested subsections —
     * so the parent badge still reflects subsection selections while the subsection is collapsed.
     */
    RentalSalesFinderFilterListComponent.prototype.sectionBadgeCount = function (section) {
        var _this = this;
        var count = section.options.length ? this.selectedCount(section.key) : 0;
        (section.subsections || []).forEach(function (sub) { return count += _this.selectedCount(sub.key); });
        return count;
    };
    RentalSalesFinderFilterListComponent.prototype.trackByChip = function (index, chip) {
        return chip.value;
    };
    /**
     * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
     * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
     * summarized by {@link overflowCount}.
     */
    RentalSalesFinderFilterListComponent.prototype._recomputeChips = function () {
        var _this = this;
        var chips = [];
        var collect = function (key, options) {
            var selected = _this.rentalSalesFinderFilter[key] || [];
            options.forEach(function (option) {
                if (selected.indexOf(option.value) !== -1) {
                    chips.push({ group: key, value: option.value, label: option.label });
                }
            });
        };
        this.sections.forEach(function (section) {
            // section.key is 'salesSection' for the options-less Sales wrapper; collect() is a no-op there since
            // section.options is empty, so the cast is safe.
            collect(section.key, section.options);
            (section.subsections || []).forEach(function (sub) { return collect(sub.key, sub.options); });
        });
        this.visibleChips = chips.slice(0, this.maxChips);
        this.overflowCount = Math.max(0, chips.length - this.maxChips);
    };
    RentalSalesFinderFilterListComponent.prototype._emit = function () {
        // Keep the query's filter in sync as the user edits, so a business name entered here counts toward search
        // validation (see SearchQuery.validate) even before the query is serialized/submitted.
        if (this.query) {
            this.query.rentalSalesFinderFilter = this.rentalSalesFinderFilter;
        }
        this._rentalSalesFinderFiltersService.updateRentalSalesFinderFilters(this.rentalSalesFinderFilter);
        this.filtersChange.emit();
    };
    return RentalSalesFinderFilterListComponent;
}());
export { RentalSalesFinderFilterListComponent };
